import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { leadsAPI } from '@/lib/api';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, Check, Home, Calendar, Ruler } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_xalawnsplus/artifacts/gh7eo36y_a0d14065-cb1f-449b-a4f6-38140797df8a.png";

export default function EstimatePage() {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    lot_size: '',
    services: [],
    frequency: '',
    additional_info: ''
  });

  const services = [
    { id: 'mowing', label: 'Lawn Mowing' },
    { id: 'edging', label: 'Edging & Trimming' },
    { id: 'leaf_removal', label: 'Leaf Removal' },
    { id: 'weed_control', label: 'Weed Control' },
    { id: 'other', label: 'Other Services' }
  ];

  const handleServiceToggle = (serviceId) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.includes(serviceId)
        ? prev.services.filter(s => s !== serviceId)
        : [...prev.services, serviceId]
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const message = `
Property: ${formData.address}
Lot Size: ${formData.lot_size}
Services: ${formData.services.join(', ')}
Frequency: ${formData.frequency}
Additional Info: ${formData.additional_info}
      `.trim();

      await leadsAPI.create({
        name: formData.name,
        email: formData.email || null,
        phone: formData.phone,
        address: formData.address,
        inquiry_type: 'quote',
        message
      });
      
      setSubmitted(true);
      toast.success('Estimate request submitted! We\'ll contact you shortly.');
    } catch (error) {
      toast.error('Failed to submit request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const canProceed = () => {
    switch(step) {
      case 1:
        return formData.name && formData.phone;
      case 2:
        return formData.address && formData.lot_size;
      case 3:
        return formData.services.length > 0 && formData.frequency;
      default:
        return false;
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="max-w-md mx-auto px-4 text-center">
          <div className="w-20 h-20 rounded-full bg-lime-500 flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-black" />
          </div>
          <h1 className="font-heading text-3xl font-bold text-white uppercase mb-4">
            Request Submitted!
          </h1>
          <p className="text-gray-400 mb-8">
            Thank you for your interest! One of our team members will contact you shortly to discuss your estimate.
          </p>
          <Link to="/">
            <Button className="btn-primary" data-testid="back-home-btn">
              Back to Home
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      {/* Header */}
      <header className="border-b border-[#262626] bg-black/50 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center gap-3">
              <img src={LOGO_URL} alt="XA's Lawns" className="h-12" />
            </Link>
            <Link to="/" className="text-gray-400 hover:text-white flex items-center gap-2 transition-colors">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
          </div>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-12 md:py-20">
        <div className="text-center mb-10">
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white uppercase mb-4">
            Get Your Free <span className="text-lime-500">Estimate</span>
          </h1>
          <p className="text-gray-400 text-lg">
            Fill out the form below and we'll get back to you with a customized quote.
          </p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-4 mb-10">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center">
              <div 
                className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition-colors ${
                  step >= s 
                    ? 'bg-lime-500 text-black' 
                    : 'bg-[#262626] text-gray-500'
                }`}
              >
                {step > s ? <Check className="w-5 h-5" /> : s}
              </div>
              {s < 3 && (
                <div className={`w-16 h-1 ${step > s ? 'bg-lime-500' : 'bg-[#262626]'}`} />
              )}
            </div>
          ))}
        </div>

        {/* Form Card */}
        <div className="card-dark p-8">
          {/* Step 1: Contact Info */}
          {step === 1 && (
            <div className="space-y-6 animate-fade-in">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-lime-500/20 flex items-center justify-center">
                  <Home className="w-5 h-5 text-lime-500" />
                </div>
                <h2 className="font-heading text-xl font-bold text-white uppercase">Contact Information</h2>
              </div>

              <div>
                <Label htmlFor="name" className="text-white">Full Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="input-dark mt-2"
                  placeholder="Your full name"
                  data-testid="estimate-name-input"
                />
              </div>

              <div>
                <Label htmlFor="phone" className="text-white">Phone Number *</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="input-dark mt-2"
                  placeholder="(555) 123-4567"
                  data-testid="estimate-phone-input"
                />
              </div>

              <div>
                <Label htmlFor="email" className="text-white">Email (Optional)</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="input-dark mt-2"
                  placeholder="your@email.com"
                  data-testid="estimate-email-input"
                />
              </div>
            </div>
          )}

          {/* Step 2: Property Info */}
          {step === 2 && (
            <div className="space-y-6 animate-fade-in">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-gold-500/20 flex items-center justify-center">
                  <Ruler className="w-5 h-5 text-gold-500" />
                </div>
                <h2 className="font-heading text-xl font-bold text-white uppercase">Property Details</h2>
              </div>

              <div>
                <Label htmlFor="address" className="text-white">Property Address *</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                  className="input-dark mt-2"
                  placeholder="123 Main St, City, State ZIP"
                  data-testid="estimate-address-input"
                />
              </div>

              <div>
                <Label htmlFor="lot_size" className="text-white">Approximate Lot Size *</Label>
                <Select 
                  value={formData.lot_size} 
                  onValueChange={(value) => setFormData({...formData, lot_size: value})}
                >
                  <SelectTrigger className="input-dark mt-2" data-testid="estimate-lot-size-select">
                    <SelectValue placeholder="Select lot size" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="small">Small (under 1/4 acre)</SelectItem>
                    <SelectItem value="medium">Medium (1/4 - 1/2 acre)</SelectItem>
                    <SelectItem value="large">Large (1/2 - 1 acre)</SelectItem>
                    <SelectItem value="xlarge">Extra Large (over 1 acre)</SelectItem>
                    <SelectItem value="not_sure">I am not sure</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* Step 3: Service Selection */}
          {step === 3 && (
            <div className="space-y-6 animate-fade-in">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-lime-500/20 flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-lime-500" />
                </div>
                <h2 className="font-heading text-xl font-bold text-white uppercase">Services Needed</h2>
              </div>

              <div>
                <Label className="text-white mb-3 block">Select Services *</Label>
                <div className="grid grid-cols-2 gap-3">
                  {services.map((service) => (
                    <label
                      key={service.id}
                      className={`flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition-all ${
                        formData.services.includes(service.id)
                          ? 'border-lime-500 bg-lime-500/10'
                          : 'border-[#262626] hover:border-[#404040]'
                      }`}
                      data-testid={`service-${service.id}`}
                    >
                      <Checkbox
                        checked={formData.services.includes(service.id)}
                        onCheckedChange={() => handleServiceToggle(service.id)}
                      />
                      <span className={formData.services.includes(service.id) ? 'text-white' : 'text-gray-400'}>
                        {service.label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <Label htmlFor="frequency" className="text-white">Service Frequency *</Label>
                <Select 
                  value={formData.frequency} 
                  onValueChange={(value) => setFormData({...formData, frequency: value})}
                >
                  <SelectTrigger className="input-dark mt-2" data-testid="estimate-frequency-select">
                    <SelectValue placeholder="How often do you need service?" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="biweekly">Every Two Weeks</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="one-time">One-Time Service</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="additional" className="text-white">Additional Information (Optional)</Label>
                <Textarea
                  id="additional"
                  value={formData.additional_info}
                  onChange={(e) => setFormData({...formData, additional_info: e.target.value})}
                  className="input-dark mt-2 min-h-[100px]"
                  placeholder="Any special requests or details we should know..."
                  data-testid="estimate-additional-input"
                />
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8 pt-6 border-t border-[#262626]">
            {step > 1 ? (
              <Button 
                variant="outline" 
                onClick={() => setStep(step - 1)}
                className="border-[#404040] text-white hover:bg-[#262626]"
                data-testid="estimate-back-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            ) : (
              <div />
            )}

            {step < 3 ? (
              <Button 
                onClick={() => setStep(step + 1)}
                disabled={!canProceed()}
                className="btn-primary"
                data-testid="estimate-next-btn"
              >
                Next
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button 
                onClick={handleSubmit}
                disabled={!canProceed() || loading}
                className="btn-primary"
                data-testid="estimate-submit-btn"
              >
                {loading ? 'Submitting...' : 'Submit Request'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
